import { useI18n } from 'vue-i18n';
import type { EPITypeKey } from '~/types/epi';
import type { EnergyTypes } from '~/types/new/energy';

export const useConstants = () => {
  const { t } = useI18n();

  const incidentDeleteReason = computed(() => [
    { key: 'yes', label: t('incident.yes_it_is_a_drift') },
    { key: 'no', label: t('incident.no_it_is_not_a drift') },
  ]);

  const energyTypeIcon = (energyType: EnergyTypes) => {
    switch (energyType) {
      case 'Electricity':
        return 'Zap';
      case 'Gas':
        return 'Flame';
      case 'Water':
        return 'Droplets';
      default:
        return 'Atom';
    }
  };

  const daysAsOptions = [
    { key: '1', label: t('global.days_list.short.monday') },
    { key: '2', label: t('global.days_list.short.tuesday') },
    { key: '3', label: t('global.days_list.short.wednesday') },
    { key: '4', label: t('global.days_list.short.thursday') },
    { key: '5', label: t('global.days_list.short.friday') },
    { key: '6', label: t('global.days_list.short.saturday') },
    { key: '7', label: t('global.days_list.short.sunday') },
  ];

  const daysListAsOptions = [
    { key: 'monday', label: t('global.days_list.monday') },
    { key: 'tuesday', label: t('global.days_list.tuesday') },
    { key: 'wednesday', label: t('global.days_list.wednesday') },
    { key: 'thursday', label: t('global.days_list.thursday') },
    { key: 'friday', label: t('global.days_list.friday') },
    { key: 'saturday', label: t('global.days_list.saturday') },
    { key: 'sunday', label: t('global.days_list.sunday') },
  ];

  const monthsAsOptions = computed(() => {
    return Array.from({ length: 12 }, (_, i) => ({ key: i + 1, label: t(`global.months.${i + 1}`) }));
  });

  const hoursAsOptions = computed(() => {
    return Array.from({ length: 24 }, (_, i) => ({ key: i, label: `${i}` }));
  });

  const minutesAsOptions = computed(() => {
    return Array.from({ length: 12 }, (_, i) => ({ key: i * 5, label: `${i * 5}` }));
  });

  const reportTypeAsOptions = computed(() => [
    {
      key: 'production',
      label: t('area.production'),
    },
    {
      key: 'fabrication_order',
      label: t('area.fabrication_order'),
    },
  ]);

  const contractTypeAsOptions = computed(() => [
    {
      label: t('contract.fixed_rate'),
      key: 'fixed',
    },
    {
      label: t('contract.flexible_rate'),
      key: 'flexible',
    },
  ]);

  const priority = [
    {
      key: 'urgent',
      label: t('incidents.priority_urgent'),
    },
    {
      key: 'high',
      label: t('incidents.priority_high'),
    },
    {
      key: 'medium',
      label: t('incidents.priority_medium'),
    },
    {
      key: 'low',
      label: t('incidents.priority_low'),
    },
  ];

  const areasAsOptions = () => {
    return areaStore()
      .getAreas.filter((area: any) => area.meters.length > 0)
      .map((area: any) => {
        return {
          key: area.id,
          label: area.name,
        };
      });
  };

  const metersAsOptions = (areaId: number) => {
    const selectedArea = areaStore().getAreas.find((area: any) => area.id === areaId);

    if (!selectedArea) return [];
    return selectedArea.meters?.map((meter) => ({
      key: meter?.id,
      label: t(`global.energy_type.${meter?.meter_type?.energy_type}`),
    }));
  };

  const alertTypeOptions = (type?: string) => {
    const alertType = alertStore()
      .getAlertAnalyticType.filter((alertType: any) => (type ? alertType.value === type : true))
      .map((alertType: any) => {
        return {
          key: alertType.value,
          label: t(`alerts.type_${alertType.value}`),
        };
      });

    return alertType;
  };

  const userAsOptions = () => {
    return userStore().getUsers.map((user: any) => {
      return {
        key: user.id,
        label: user.first_name + ' ' + user.last_name,
      };
    });
  };

  const energyTypeAsOptions = computed(() => {
    return [
      {
        key: 'Electricity',
        label: t('global.energy_type.Electricity'),
      },
      {
        key: 'Gas',
        label: t('global.energy_type.Gas'),
      },
    ];
  });

  const globalEPIOptions = computed((): { key: EPITypeKey; label: string }[] => [
    { key: 'cost', label: t('global.per_cost') },
    { key: 'quantity', label: t('global.per_quantity') },
    { key: 'co2', label: t('global.per_co2') },
  ]);

  const globalProductionEPIsUnitsFromAreaId = (areaId: number): { key: EPITypeKey; label: string }[] => {
    const area = areaStore().getAreas.find((area) => area.id === areaId);
    return [
      { key: 'cost', label: ` € / ${area?.production_unit?.symbol ?? '--'}` },
      { key: 'quantity', label: ` kWh / ${area?.production_unit?.symbol ?? '--'}` },
      { key: 'co2', label: ` gCo2 / ${area?.production_unit?.symbol ?? '--'}` },
    ];
  };

  return {
    reportTypeAsOptions,
    incidentDeleteReason,
    priority,
    areasAsOptions,
    alertTypeOptions,
    metersAsOptions,
    userAsOptions,
    globalProductionEPIsUnitsFromAreaId,
    energyTypeIcon,
    monthsAsOptions,
    daysAsOptions,
    hoursAsOptions,
    minutesAsOptions,
    energyTypeAsOptions,
    contractTypeAsOptions,
    globalEPIOptions,
    daysListAsOptions,
  };
};
